import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Upload } from "lucide-react";
import * as adminApi from "@/api/admin";
import { VIDEO_PAGES } from "./types";

function PageVideoRow({ slug, label }: { slug: string; label: string }) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    adminApi.getPageVideo(slug)
      .then(setVideoUrl)
      .catch(() => setVideoUrl(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [slug]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    await adminApi.uploadPageVideo(slug, file);
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
    setUploading(false);
    load();
  };

  const handleDelete = async () => {
    setDeleting(true);
    await adminApi.deletePageVideo(slug);
    setDeleting(false);
    setConfirmDelete(false);
    load();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        {loading ? (
          <span className="text-xs text-gray-400">Loading…</span>
        ) : videoUrl ? (
          <span className="text-[10px] font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Active</span>
        ) : (
          <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded">No video</span>
        )}
      </div>

      {videoUrl && !loading && (
        <video key={videoUrl} src={videoUrl} controls className="w-full max-h-64 rounded-md bg-black" />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          onChange={e => setFile(e.target.files?.[0] ?? null)}
          className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-gray-100 file:text-gray-700 file:cursor-pointer hover:file:bg-gray-200"
        />
        <Button
          type="button"
          onClick={handleUpload}
          disabled={!file || uploading}
          className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} className="mr-1" /> {videoUrl ? 'Replace' : 'Upload'}</>}
        </Button>
        {videoUrl && !confirmDelete && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
        )}
        {confirmDelete && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-600">Delete current video?</span>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              className="px-3 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="px-3 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PageVideosView() {
  return (
    <div className="space-y-4">
      {VIDEO_PAGES.map(p => <PageVideoRow key={p.slug} slug={p.slug} label={p.label} />)}
    </div>
  );
}
